// src/components/settings/catalog/CatalogFolderTree.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// The folder column: All items, Ungrouped, the folder tree (sub-folders open and close), then
// Archived under a line. "+" makes a folder — at the top level, or inside the folder that's open.
// Moving folders is not offered (Stage 3). Counts come from useFolderCounts; a count still on
// its way simply isn't shown.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Layers, Inbox, Archive, Plus, Star } from 'lucide-react';
import { BTN_GHOST, BTN_PRIMARY, INPUT, ErrorLine } from './catalogUiParts';

const rowClass = (active) => `flex-1 min-w-0 flex items-center gap-2 min-h-[44px] pr-2 rounded-lg text-sm text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${
    active ? 'bg-[#5F4EC9]/10 text-[#5F4EC9] font-semibold' : 'text-gray-700 hover:bg-gray-100'}`;

function Count({ n }) {
    if (typeof n !== 'number') return null;
    return <span className="ml-auto pl-2 text-xs text-gray-400 tabular-nums">{n.toLocaleString('en-US')}</span>;
}

export default function CatalogFolderTree({ tree, selection, onSelect, counts, expanded, onToggle, onCreate }) {
    const [draft, setDraft] = useState(null); // null | { parentId, name, saving, error }
    const inputRef = useRef(null);
    const openFolder = selection.kind === 'folder' ? tree.byId.get(selection.id) : null;

    useEffect(() => { if (draft && inputRef.current) inputRef.current.focus(); }, [draft && draft.parentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const startNew = () => setDraft({ parentId: openFolder ? openFolder.id : null, name: '', saving: false, error: '' });
    const create = async () => {
        const name = draft.name.trim();
        if (!name) { setDraft((d) => ({ ...d, error: 'Please give the folder a name.' })); return; }
        setDraft((d) => ({ ...d, saving: true, error: '' }));
        const err = await onCreate(draft.parentId, name);
        if (err) setDraft((d) => ({ ...d, saving: false, error: err }));
        else setDraft(null);
    };

    const renderFolder = (f, depth) => {
        const kids = tree.children(f.id);
        const open = expanded.has(f.id);
        const active = selection.kind === 'folder' && selection.id === f.id;
        return (
            <li key={f.id}>
                <div className="flex items-center" style={{ paddingLeft: depth * 14 }}>
                    {kids.length ? (
                        <button type="button" className="shrink-0 w-11 h-11 inline-flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40"
                            aria-label={`${open ? 'Close' : 'Open'} ${f.name}`} aria-expanded={open} onClick={() => onToggle(f.id)}>
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                    ) : <span className="shrink-0 w-11" aria-hidden="true" />}
                    <button type="button" className={rowClass(active)} aria-current={active ? 'true' : undefined} onClick={() => onSelect({ kind: 'folder', id: f.id })}>
                        {active ? <FolderOpen size={16} className="shrink-0" /> : <Folder size={16} className="shrink-0 text-gray-400" />}
                        <span className="truncate">{f.name}</span>
                        <Count n={counts[`f:${f.id}`]} />
                    </button>
                </div>
                {open && kids.length > 0 && <ul>{kids.map((k) => renderFolder(k, depth + 1))}</ul>}
            </li>
        );
    };

    const top = tree.children(null);
    const parentName = draft && draft.parentId ? (tree.byId.get(draft.parentId) || {}).name : null;

    return (
        <nav aria-label="Folders" className="space-y-1">
            <div className="flex items-center justify-between pl-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Folders</h3>
                <button type="button" className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-[#5F4EC9] hover:bg-[#5F4EC9]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40"
                    aria-label={openFolder ? `New folder inside ${openFolder.name}` : 'New folder'} title={openFolder ? `New folder inside ${openFolder.name}` : 'New folder'}
                    onClick={startNew} disabled={!!draft}>
                    <Plus size={18} />
                </button>
            </div>

            {draft && (
                <div className="p-3 rounded-lg border border-[#5F4EC9]/30 bg-[#5F4EC9]/[0.03] space-y-2">
                    <label htmlFor="catalog-new-folder" className="block text-sm font-medium text-gray-700">
                        {parentName ? `New folder inside ${parentName}` : 'New folder'}
                    </label>
                    <input id="catalog-new-folder" ref={inputRef} className={INPUT} value={draft.name} maxLength={100} placeholder="Folder name"
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value, error: '' }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') create(); if (e.key === 'Escape') setDraft(null); }} />
                    <ErrorLine text={draft.error} />
                    <div className="flex gap-2">
                        <button type="button" className={BTN_PRIMARY} disabled={draft.saving} onClick={create}>{draft.saving ? 'Creating…' : 'Create'}</button>
                        <button type="button" className={BTN_GHOST} disabled={draft.saving} onClick={() => setDraft(null)}>Cancel</button>
                    </div>
                </div>
            )}

            <ul className="space-y-0.5">
                <li className="flex">
                    <button type="button" className={`${rowClass(selection.kind === 'all')} pl-3`} onClick={() => onSelect({ kind: 'all' })}>
                        <Layers size={16} className="shrink-0" /> All items <Count n={counts.all} />
                    </button>
                </li>
                {/* catscreen-7a2c4e91 Stage 4 — the company's starred items */}
                <li className="flex">
                    <button type="button" className={`${rowClass(selection.kind === 'favorites')} pl-3`} onClick={() => onSelect({ kind: 'favorites' })}>
                        <Star size={16} className={`shrink-0 ${selection.kind === 'favorites' ? 'fill-amber-400 text-amber-500' : 'text-amber-500'}`} /> Favorites <Count n={counts.favorites} />
                    </button>
                </li>
                <li className="flex">
                    <button type="button" className={`${rowClass(selection.kind === 'ungrouped')} pl-3`} onClick={() => onSelect({ kind: 'ungrouped' })}>
                        <Inbox size={16} className="shrink-0" /> Ungrouped <Count n={counts.ungrouped} />
                    </button>
                </li>
                {top.map((f) => renderFolder(f, 0))}
            </ul>
            {top.length === 0 && !draft && <p className="px-3 text-xs text-gray-400">No folders yet. Use + to make one.</p>}

            <div className="pt-2 mt-2 border-t border-gray-200">
                <button type="button" className={`${rowClass(selection.kind === 'archived')} pl-3 w-full`} onClick={() => onSelect({ kind: 'archived' })}>
                    <Archive size={16} className="shrink-0" /> Archived <Count n={counts.archived} />
                </button>
            </div>
        </nav>
    );
}
